import {
	useCallback,
	useEffect,
	useId,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { CompactButton } from "../../../../../../shared/components/buttons/compact-button/CompactButton";
import { hideModal } from "../../../../../../shared/ui/gsap-animations/hide-modal";
import { showModal } from "../../../../../../shared/ui/gsap-animations/show-modal";
import {
	markHelloModalShown,
	wasHelloModalShown,
} from "./utils/hello-modal-storage";
import "./hello-modal.scss";

type HelloModalProps = {
	onOpenDocs: () => void | Promise<void>;
};

const getFocusableElements = (root: HTMLElement) => {
	const selector =
		'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
	return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
		(el) => !el.hasAttribute("disabled") && el.tabIndex !== -1,
	);
};

export const HelloModal = ({ onOpenDocs }: HelloModalProps) => {
	const titleId = useId();
	const descriptionId = useId();
	const overlayRef = useRef<HTMLDivElement | null>(null);
	const panelRef = useRef<HTMLDivElement | null>(null);
	const enterTlRef = useRef<ReturnType<typeof showModal> | null>(null);
	const isClosingRef = useRef(false);
	const afterCloseRef = useRef<(() => void) | null>(null);

	const [isOpen, setIsOpen] = useState(false);

	useEffect(() => {
		if (!wasHelloModalShown()) {
			setIsOpen(true);
		}
	}, []);

	const finishClose = useCallback(() => {
		isClosingRef.current = false;
		setIsOpen(false);
		const afterClose = afterCloseRef.current;
		afterCloseRef.current = null;
		afterClose?.();
	}, []);

	const handleDismiss = useCallback(
		(options?: { afterClose?: () => void }) => {
			if (isClosingRef.current) return;
			markHelloModalShown();
			afterCloseRef.current = options?.afterClose ?? null;

			const overlay = overlayRef.current;
			const panel = panelRef.current;
			if (!overlay || !panel) {
				finishClose();
				return;
			}

			isClosingRef.current = true;
			enterTlRef.current?.kill();
			enterTlRef.current = null;
			hideModal(overlay, panel, {
				y: 16,
				onComplete: finishClose,
			});
		},
		[finishClose],
	);

	const handleLater = useCallback(() => {
		handleDismiss();
	}, [handleDismiss]);

	const handleOpenDocs = useCallback(() => {
		handleDismiss({
			afterClose: () => {
				void onOpenDocs();
			},
		});
	}, [handleDismiss, onOpenDocs]);

	useLayoutEffect(() => {
		if (!isOpen) return;
		const overlay = overlayRef.current;
		const panel = panelRef.current;
		if (!overlay || !panel) return;

		enterTlRef.current = showModal(overlay, panel, { y: 16 });
		return () => {
			enterTlRef.current?.kill();
			enterTlRef.current = null;
		};
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) return;

		const prevActive = document.activeElement as HTMLElement | null;
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";

		const focusInitial = () => {
			const panel = panelRef.current;
			if (!panel) return;
			const focusables = getFocusableElements(panel);
			(focusables[0] ?? panel).focus();
		};

		const raf = window.requestAnimationFrame(focusInitial);

		const onKeyDown = (e: KeyboardEvent) => {
			if (!panelRef.current) return;

			if (e.key === "Escape") {
				e.preventDefault();
				handleLater();
				return;
			}

			if (e.key === "Tab") {
				const focusables = getFocusableElements(panelRef.current);
				if (focusables.length === 0) {
					e.preventDefault();
					panelRef.current.focus();
					return;
				}

				const first = focusables[0];
				const last = focusables[focusables.length - 1];
				const active = document.activeElement as HTMLElement | null;

				if (e.shiftKey) {
					if (
						!active ||
						active === first ||
						!panelRef.current.contains(active)
					) {
						e.preventDefault();
						last.focus();
					}
				} else {
					if (active === last) {
						e.preventDefault();
						first.focus();
					}
				}
			}
		};

		document.addEventListener("keydown", onKeyDown);

		return () => {
			window.cancelAnimationFrame(raf);
			document.removeEventListener("keydown", onKeyDown);
			document.body.style.overflow = prevOverflow;
			prevActive?.focus?.();
		};
	}, [isOpen, handleLater]);

	if (!isOpen) return null;

	return createPortal(
		<div className='hello-modal' aria-hidden={false}>
			<div
				ref={overlayRef}
				className='hello-modal__overlay'
				onMouseDown={handleLater}
			/>
			<div
				ref={panelRef}
				className='hello-modal__panel'
				role='dialog'
				aria-modal='true'
				aria-labelledby={titleId}
				aria-describedby={descriptionId}
				tabIndex={-1}>
				<h2 className='hello-modal__title' id={titleId}>
					Ласкаво просимо!
				</h2>
				<p className='hello-modal__description' id={descriptionId}>
					Натисніть Доки, щоб переглянути документацію та дізнатися про всі
					можливості системи.
				</p>
				<div className='hello-modal__actions'>
					<CompactButton
						onClick={handleOpenDocs}
						type='button'
						theme='green'>
						Відкрити Доки
					</CompactButton>
					<CompactButton onClick={handleLater} type='button' theme='default'>
						Пізніше
					</CompactButton>
				</div>
			</div>
		</div>,
		document.body,
	);
};
